// src/App.tsx
import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { Routes, Route } from 'react-router-dom';

import Footer from './components/Footer';
import Header from './components/Header';
import GoogleAnalytics from './components/GoogleAnalytics';
import { GeoLocationProvider } from './contexts/GeoLocationContext';
import { initializeBillPages } from './utils/billConfig';
import About from './pages/About';
import ScheduleConsultation from './pages/ScheduleConsultation';
import BillDetailPage from './pages/BillDetailPage';
import BillRouter from './components/BillRouter';
import ScrollToTop from './components/ScrollToTop';
import BillTwoSegmentRouter from './components/BillTwoSegmentRouter';
import Bills from './pages/Bills';
import Home from './pages/Home';
import FamilyFirstFederalReform from './pages/FamilyFirstFederalReform';
import JoinTheTeam from './pages/JoinTheTeam';
import LegislatorMeetings from './pages/LegislatorMeetings';
import LegislatorLookup from './pages/LegislatorLookup';
import NotFound from './pages/NotFound';
import FAQ from './pages/FAQ';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import CommemorativeCoins from './pages/CommemorativeCoins';

interface AppProps {
  toggleTheme: () => void;
  darkMode: boolean;
}

const App: React.FC<AppProps> = ({ toggleTheme, darkMode }) => {
  // Initialize bill pages from spreadsheet on app startup
  useEffect(() => {
    initializeBillPages();
  }, []);

  // Temporary: hardcode GA ID to eliminate env var issues
  const gaTrackingId = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-6T416ZYYYR';

  return (
    <GeoLocationProvider>
      <ScrollToTop />
      <GoogleAnalytics measurementId={gaTrackingId} />
      <Header toggleTheme={toggleTheme} darkMode={darkMode} />
      <Box id="page-content" tabIndex={-1} sx={{ outline: 'none' }}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about-robert-garza" element={<About />} />
          <Route path="/schedule-consultation" element={<ScheduleConsultation />} />
          <Route path="/family-court-reform-bills" element={<Bills />} />
          <Route path="/family-first-federal-reform" element={<FamilyFirstFederalReform />} />
          <Route path="/join-the-team" element={<JoinTheTeam />} />
          <Route path="/legislator-meetings" element={<LegislatorMeetings />} />
          <Route path="/legislator-lookup" element={<LegislatorLookup />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/commemorative-coins" element={<CommemorativeCoins />} />

          {/* Dynamic Bill Pages — 3-segment: country + state + bill (unambiguous) */}
          <Route path="/bill/:country/:state/:billSlug" element={<BillDetailPage />} />

          {/* 2-segment: runtime disambiguation via BillTwoSegmentRouter */}
          <Route path="/bill/:seg1/:seg2" element={<BillTwoSegmentRouter />} />

          {/* 1-segment: runtime disambiguation via BillRouter */}
          <Route path="/bill/:param" element={<BillRouter />} />

          {/* Explicit 404 route for react-snap prerendering */}
          <Route path="/404" element={<NotFound />} />

          {/* Catch-all route for 404 pages - must be LAST */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Box>
      <Footer />
    </GeoLocationProvider>
  );
};

export default App;
